/**
 * Pure helper for parsing the ingredient-quantity text input (ATL-633 / Sentry 29R).
 *
 * Kept in a standalone, dependency-free module so it is trivially unit-testable (no jotai /
 * AtomsProvider import chain) and reusable by the ingredient editor.
 */

/**
 * Parse a raw ingredient-quantity input into either a Number (completed value) or a String
 * (still mid-typing a decimal, so the trailing separator / zero stays visible in the field).
 *
 * The backend IngredientDto.quantity is @IsNumber(), so a completed integer like "10" / "100"
 * MUST become a Number — the previous heuristic kept ANY value ending in "0" as a string,
 * which wrongly caught "10"/"100"/"20" and failed dual-write validation (ATL-633 / Sentry 29R).
 *
 * Keep as a string ONLY while genuinely mid-typing:
 *  - ends with a decimal separator ("." / ","), e.g. "1."
 *  - ends with "0" but a separator is already present, e.g. "1.0" / "5.50"
 * Otherwise parse to a Number (NaN falls back to the raw string so the input isn't cleared).
 *
 * @param {string|number} text - The raw input value
 * @returns {string|number} A Number for completed values, or a String while mid-typing
 */
export const parseIngredientQuantityInput = (text) => {
  const textStr = String(text)
  if (textStr === '' || textStr === '.' || textStr === ',') {
    return textStr
  }
  const lastChar = textStr.charAt(textStr.length - 1)
  const hasDecimalSeparator = textStr.includes('.') || textStr.includes(',')
  if (lastChar === '.' || lastChar === ',' || (lastChar === '0' && hasDecimalSeparator)) {
    return textStr
  }
  // Normalize the comma decimal separator (Russian/Kazakh locale keypads emit ',') before
  // parseFloat, which otherwise stops AT the comma — "1,5" would parse to 1, silently dropping
  // the fraction and corrupting recipe quantities (ATL-633 review).
  const parsed = parseFloat(textStr.replace(',', '.'))
  return isNaN(parsed) ? textStr : parsed
}

/**
 * Coerce a possibly-string, possibly-comma-decimal numeric input to a FINITE Number, or `null`
 * when it cannot be parsed at all.
 *
 * The single comma-aware coercion used by every dual-write payload that has to satisfy a backend
 * `@IsNumber()` DTO from editor state ("1,5" → 1.5, not 1 — ATL-633). It returns `null` rather
 * than picking a fallback because the two callers must handle "unparseable" DIFFERENTLY, and
 * baking one policy in would be wrong for the other:
 *  - `ingredientQuantityToNumber()` maps it to 0 — a recipe quantity of 0 is inert.
 *  - the `storeStock` Supabase mirror (ATL-762, products.ts) SKIPS the store entirely — sending 0
 *    there would zero a real customer's stock, which is exactly the `|| 0` shape that is forbidden
 *    on a money/stock value.
 *
 * @param {unknown} value - Raw value from editor state / an imported sheet
 * @returns {number|null} A finite Number, or null when the value cannot be parsed
 */
export const parseDecimalValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (value === null || value === undefined) {
    return null
  }
  const parsed = parseFloat(String(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Coerce an ingredient quantity (possibly a comma-decimal or mid-typed string) to a finite
 * Number for the backend IngredientDto (@IsNumber()). Comma is normalized to a dot so "1,5"
 * → 1.5 (not 1); a value that still can't parse falls back to 0.
 *
 * Used by the product dual-write payload so Supabase always receives the same numeric quantity
 * the user intended, matching the Firestore source of truth.
 *
 * @param {string|number} value - The ingredient quantity from editor state
 * @returns {number} A finite Number (0 when unparseable)
 */
export const ingredientQuantityToNumber = (value) => {
  const parsed = parseDecimalValue(value)
  return parsed === null ? 0 : parsed
}
